/**
 * bands — classificação de métrica elétrica em faixa operacional.
 *
 * Módulo PURO: sem React, sem DOM, sem fetch. Existe separado porque é a
 * única regra desta tela que tem consequência de leitura (verde/amarelo/
 * vermelho) e por isso precisa ser testável sem montar componente.
 *
 * ---------------------------------------------------------------------------
 * DUAS CORREÇÕES DE CONTEÚDO sobre a tela de referência (SBVolt), exigidas
 * pela spec §6:
 *
 * 1. O texto "dentro do padrão ANEEL" NÃO pode estar atrelado a FP >= 0,95.
 *    O piso regulatório é 0,92. A faixa 0,92–0,95 é ATENÇÃO OPERACIONAL,
 *    não violação — e o rótulo diz exatamente isso.
 * 2. As faixas de THD de 3 % e 5 % são OPERACIONAIS desta operação, e não
 *    limites do PRODIST Módulo 8. Nunca rotular como "limite ANEEL".
 *
 * ---------------------------------------------------------------------------
 * WCAG 1.4.1: a cor nunca é o único portador do significado. Para cada
 * combinação (métrica, faixa) existe `bandLabel()`, e é ele que vai para a
 * tela ao lado do número. A cor é reforço, não a informação.
 *
 * A faixa 'crit' do simulador nunca é exercitada por dado na tela — o
 * simulador é capado em 85 % de carga (spec §4, nota deliberada), porque
 * exibir violação crítica permanente sem alarme correspondente é
 * precisamente o BUG-SANITY que o CLAUDE.md manda barrar. 'crit' existe
 * aqui para o modo REAL e é coberta por teste unitário.
 */

import { autoScale, type SubstationBaseUnit, type SubstationScaledUnit } from '@nxvolt/shared';

export type Band = 'ok' | 'warn' | 'crit';

/**
 * Métricas com faixa definida. Deliberadamente fechado: métrica sem faixa
 * acordada (Q, S, demanda, energia) é impressa como número, sem semáforo —
 * pintar de verde algo cujo "bom" ninguém definiu é decoração, não leitura.
 */
export type BandMetric = 'powerFactor' | 'load' | 'thd' | 'frequency';

/**
 * Faixas (spec §6):
 *
 * | métrica          | ok            | atenção        | crítico    |
 * |------------------|---------------|----------------|------------|
 * | powerFactor      | >= 0,95       | >= 0,92        | < 0,92     |
 * | load (% do kVA)  | < 70          | 70 a 89,9      | >= 90      |
 * | thd (V e I, %)   | <= 3          | <= 5           | > 5        |
 * | frequency (Hz)   | 59,9 a 60,1   | 59,5 a 60,5    | fora       |
 *
 * `powerFactor` recebe a grandeza adimensional 0..1 (como no contrato da
 * API), não porcentagem.
 */
export function bandFor(metric: BandMetric, value: number): Band {
  switch (metric) {
    case 'powerFactor':
      if (value >= 0.95) return 'ok';
      if (value >= 0.92) return 'warn';
      return 'crit';
    case 'load':
      if (value < 70) return 'ok';
      if (value < 90) return 'warn';
      return 'crit';
    case 'thd':
      if (value <= 3) return 'ok';
      if (value <= 5) return 'warn';
      return 'crit';
    case 'frequency':
      if (value >= 59.9 && value <= 60.1) return 'ok';
      if (value >= 59.5 && value <= 60.5) return 'warn';
      return 'crit';
    default: {
      // Exaustividade verificada em tempo de compilação: se alguém
      // acrescentar uma métrica ao union e esquecer o case, isto não compila.
      const exhaustive: never = metric;
      return exhaustive;
    }
  }
}

/** Rótulo textual da faixa — o portador não-cromático do significado. */
export function bandLabel(metric: BandMetric, band: Band): string {
  switch (metric) {
    case 'powerFactor':
      if (band === 'ok') return 'Acima do mínimo regulatório (0,92)';
      if (band === 'warn') return 'Atenção operacional — acima do mínimo regulatório (0,92)';
      return 'Abaixo do mínimo regulatório (0,92)';
    case 'load':
      if (band === 'ok') return 'Carga normal';
      if (band === 'warn') return 'Carga elevada';
      return 'Carga crítica';
    case 'thd':
      if (band === 'ok') return 'Dentro da faixa operacional (até 3 %)';
      if (band === 'warn') return 'Atenção — faixa operacional de 3 a 5 %';
      return 'Acima da faixa operacional (5 %)';
    case 'frequency':
      if (band === 'ok') return 'Frequência nominal';
      if (band === 'warn') return 'Desvio de frequência';
      return 'Frequência fora de faixa';
    default: {
      const exhaustive: never = metric;
      return exhaustive;
    }
  }
}

/** Classe CSS do modificador de faixa. Uma função só, para o nome da classe
 *  não ser montado por concatenação ad-hoc em cinco componentes. */
export function bandClass(band: Band): string {
  return `is-${band}`;
}

/* ---------------------------------------------------------------------------
 * Formatação.
 *
 * A escala de unidade NÃO mora aqui: `autoScale()` é exportada por
 * `@nxvolt/shared` e é a FUNÇÃO ÚNICA da spec §3 — a mesma que o simulador do
 * servidor usa. Duplicá-la no front seria exatamente o que a regra proíbe:
 * o dia em que o limiar mudar de 1000 para outro valor, o eixo do gráfico e o
 * KPI passariam a discordar de unidade na mesma dobra da tela.
 * ------------------------------------------------------------------------- */

/**
 * Casas decimais de apresentação. A UI NÃO arredonda grandeza — o servidor já
 * entregou na precisão do contrato (spec §2); aqui só se escolhe como mostrar.
 *
 * DUAS casas nas unidades M, e não uma: trocar de unidade não pode custar
 * precisão. Com uma casa, 1.026 kW e 1.049 kW imprimem os dois "1,0 MW" e a
 * tela esconde 23 kW de diferença entre dois transformadores. Com duas,
 * saem "1,03 MW" e "1,05 MW".
 *
 * `kWh` na unidade base sai INTEIRO porque o contrato define `energyKwh` como
 * inteiro: imprimir "842,0 kWh" inventaria uma casa que o dado não tem.
 */
export function digitsFor(unit: SubstationScaledUnit): number {
  if (unit === 'kWh') return 0;
  return unit.startsWith('M') ? 2 : 1;
}

/**
 * Formatação pt-BR de um número solto (sem unidade).
 */
export function fmt(value: number | null, digits = 1): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * Valor escalado pela função única + unidade, pronto para impressão.
 *
 * `ref` é o valor de REFERÊNCIA DO CONJUNTO que está sendo comparado — o maior
 * valor entre os irmãos exibidos. Sem ele, dois transformadores de placa
 * idêntica saem "980,0 kW" e "1,0 MW" lado a lado e o maior parece o menor.
 * Omitir `ref` é legítimo só quando o número não é comparado com nenhum outro
 * na tela.
 */
export function fmtScaled(
  value: number | null,
  unit: SubstationBaseUnit,
  ref?: number | null,
): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const scaled = autoScale(value, unit, ref ?? undefined);
  return `${fmt(scaled.value, digitsFor(scaled.unit))} ${scaled.unit}`;
}

/**
 * Idade de leitura legível. "há 940 s" obriga o operador a dividir de cabeça;
 * acima de dois minutos a unidade útil é minuto, e acima de dois dias é DIA —
 * "há 672,1 h" para 28 dias era o mesmo defeito que o cabeçalho descreve, um
 * degrau acima. Só aparece em modo real —
 * em simulado, idade de leitura seria afirmação de vivacidade de um número
 * gerado (spec §7 ponto 7).
 */
export function fmtAge(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '—';
  /* SEM `Math.max(0, …)`, e isto é deliberado.
     O clamp que havia aqui era o SEGUNDO de uma fila de dois: o servidor
     convertia "3 h à frente do relógio" em `age 0`, e este garantia "0 s"
     mesmo depois de o servidor parar de clampar. Idade negativa passou a
     significar "carimbo à frente" no contrato, e apagá-la aqui é justamente o
     que fazia a tela chamar de "o mais fresco possível" uma leitura que não se
     pode situar no tempo.
     Quem esbarrar num número negativo e quiser "consertar" com um clamp está
     reintroduzindo o defeito — há teste de guarda proibindo. */
  const futuro = seconds < 0;
  const s = Math.round(Math.abs(seconds));
  /*
    A ESCALA ROLA ATE DIA, e ela parava em hora.

    Medido na tela: um onibus que nunca voltou a reportar em 28 dias imprimia
    "ha 672,1 h" — tres digitos e uma decimal que ninguem divide de cabeca, na
    mesma funcao cujo cabecalho diz que "ha 940 s" obriga o operador a dividir de
    cabeca. O argumento que criou o degrau de minuto para o de hora vale igual do
    de hora para o de dia; o degrau simplesmente nao tinha sido escrito.

    O CORTE em 48 h e o mesmo critério dos outros dois (dobro da unidade
    seguinte): abaixo dele "36,0 h" ainda diz algo sobre o turno, acima dele o
    operador quer saber se e "ontem" ou "tres semanas". Uma decimal em dia porque
    "28 dias" e "28,4 dias" respondem a mesma pergunta, e o segundo nao mente
    sobre precisao — a idade vem em segundos do servidor.
  */
  const minutos = Math.floor(s / 60);
  const horas = minutos / 60;
  const magnitude =
    s < 120
      ? `${fmt(s, 0)} s`
      : minutos < 120
        ? `${fmt(minutos, 0)} min`
        : horas < 48
          ? `${fmt(horas, 1)} h`
          : `${fmt(horas / 24, 1)} dias`;
  /* O sufixo é a rede: se algum chamador esquecer de tratar o estado
     'futura', imprime "há 3,0 h no futuro" — absurdo e VERDADEIRO — em vez de
     "há 0 s", que é plausível e falso. */
  return futuro ? `${magnitude} no futuro` : magnitude;
}
